import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { MaintenanceRequest } from "../../../types";
import { formatDateOnly } from "../../../utils/formatDate";

interface ScheduleViewProps {
	tasks: MaintenanceRequest[];
	onTaskSelect: (task: MaintenanceRequest) => void;
}

const ScheduleView = ({ tasks, onTaskSelect }: ScheduleViewProps) => {
	const [selectedDate, setSelectedDate] = useState(
		new Date().toISOString().split("T")[0],
	);

	// Generate calendar days for current month
	const generateCalendarDays = () => {
		const today = new Date();
		const year = today.getFullYear();
		const month = today.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const startDate = new Date(firstDay);
		startDate.setDate(startDate.getDate() - firstDay.getDay());

		const days = [];
		const current = new Date(startDate);

		for (let i = 0; i < 42; i++) {
			days.push(new Date(current));
			current.setDate(current.getDate() + 1);
		}

		return days;
	};

	const calendarDays = generateCalendarDays();
	const today = new Date();
	const currentMonth = today.getMonth();
	const currentYear = today.getFullYear();

	// Get tasks for a specific date
	const getTasksForDate = (date: Date) => {
		if (!tasks) return [];
		// Format date as YYYY-MM-DD in LOCAL timezone (not UTC)
		// This avoids timezone conversion issues
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		const dateString = `${year}-${month}-${day}`;

		// Compare dates (ignore time)
		return tasks.filter((task) => {
			if (!task.scheduledDate) return false;
			try {
				// Extract just the date part (YYYY-MM-DD) from the task date string
				// WITHOUT converting to UTC - just use the date part as-is
				const dateStr = typeof task.scheduledDate === 'string'
					? task.scheduledDate
					: task.scheduledDate.toISOString();
				const taskDatePart = dateStr.split("T")[0];
				return taskDatePart === dateString;
			} catch {
				return false;
			}
		});
	};

	// Get tasks for selected date
	const selectedDateTasks = getTasksForDate(new Date(selectedDate));

	const getPriorityColor = (priority: string) => {
		switch (priority) {
			case "high":
				return "bg-error text-error-foreground";
			case "medium":
				return "bg-warning text-warning-foreground";
			case "low":
				return "bg-success text-success-foreground";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	return (
		<div className="space-y-6">
			{/* Calendar Header */}
			<div className="bg-card border border-border rounded-lg p-6">
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-lg font-semibold text-foreground">
						{new Date().toLocaleDateString("en-US", {
							month: "long",
							year: "numeric",
						})}
					</h2>
					<div className="flex items-center space-x-2">
						<Button variant="outline" size="sm" iconName="ChevronLeft" />
						<Button variant="outline" size="sm" iconName="ChevronRight" />
					</div>
				</div>

				{/* Calendar Grid */}
				<div className="grid grid-cols-7 gap-1">
					{/* Day headers */}
					{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
						<div
							key={day}
							className="p-2 text-center text-sm font-medium text-muted-foreground"
						>
							{day}
						</div>
					))}

					{/* Calendar days */}
					{calendarDays.map((date, index) => {
						const isCurrentMonth = date.getMonth() === currentMonth;
						const isToday = date.toDateString() === today.toDateString();
						const isSelected =
							date.toISOString().split("T")[0] === selectedDate;
						const dayTasks = getTasksForDate(date);

						return (
							<button
								key={index}
								onClick={() =>
									setSelectedDate(date.toISOString().split("T")[0])
								}
								className={`p-2 text-sm rounded-lg transition-colors relative ${
									!isCurrentMonth
										? "text-muted-foreground/50"
										: isSelected
										? "bg-primary text-primary-foreground"
										: isToday
										? "bg-primary/10 text-primary font-semibold"
										: "hover:bg-muted text-foreground"
								}`}
							>
								<div>{date.getDate()}</div>
								{dayTasks.length > 0 && (
									<div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex space-x-1">
										{dayTasks.slice(0, 3).map((task, taskIndex) => (
											<div
												key={taskIndex}
												className={`w-1.5 h-1.5 rounded-full ${
													task.priority === "high"
														? "bg-error"
														: task.priority === "medium"
														? "bg-warning"
														: "bg-success"
												}`}
											/>
										))}
										{dayTasks.length > 3 && (
											<div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
										)}
									</div>
								)}
							</button>
						);
					})}
				</div>
			</div>

			{/* Selected Date Tasks */}
			<div className="bg-card border border-border rounded-lg">
				<div className="p-6 border-b border-border">
					<h3 className="text-lg font-semibold text-foreground">
						Tasks for {formatDateOnly(selectedDate)}
					</h3>
					<p className="text-sm text-muted-foreground mt-1">
						{selectedDateTasks.length} task
						{selectedDateTasks.length !== 1 ? "s" : ""} scheduled
					</p>
				</div>

				<div className="p-6">
					{selectedDateTasks.length === 0 ? (
						<div className="text-center py-8">
							<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
								<Icon
									name="Calendar"
									size={24}
									className="text-muted-foreground"
								/>
							</div>
							<p className="text-muted-foreground">
								No tasks scheduled for this date
							</p>
						</div>
					) : (
						<div className="space-y-4">
							{selectedDateTasks
								.sort((a, b) => {
									// Sort by scheduled time, handling missing times
									const timeA = a.scheduledTime || "";
									const timeB = b.scheduledTime || "";
									return timeA.localeCompare(timeB);
								})
								.map((task) => (
									<div
										key={task.id}
										className="flex items-center space-x-4 p-4 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
										onClick={() => onTaskSelect(task)}
									>
										<div className="text-sm font-medium text-foreground min-w-[80px]">
											{task.scheduledTime || "Not scheduled"}
										</div>
										<div
											className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(
												task.priority,
											)}`}
										>
											{task.priority}
										</div>
										<div className="flex-1">
											<h4 className="font-medium text-foreground">
												{task.title}
											</h4>
											<p className="text-sm text-muted-foreground">
												{task.property_name || (typeof task.property === 'string' ? task.property : task.property?.name || 'Property')}
												{task.unit ? ` - ${task.unit}` : ""}
											</p>
										</div>
										<div className="text-sm text-muted-foreground">
											{task.estimatedTime || "N/A"}
										</div>
										<Icon
											name="ChevronRight"
											size={16}
											className="text-muted-foreground"
										/>
									</div>
								))}
						</div>
					)}
				</div>
			</div>

			{/* Travel Time Optimization */}
			<div className="bg-card border border-border rounded-lg p-6">
				<div className="flex items-center space-x-3 mb-4">
					<div className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
						<Icon name="MapPin" size={20} />
					</div>
					<div>
						<h3 className="font-semibold text-foreground">
							Route Optimization
						</h3>
						<p className="text-sm text-muted-foreground">
							Suggested order for maximum efficiency
						</p>
					</div>
				</div>

				{selectedDateTasks.length > 1 ? (
					<div className="space-y-2">
						{selectedDateTasks.map((task, index) => (
							<div
								key={task.id}
								className="flex items-center space-x-3 text-sm"
							>
								<div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-medium">
									{index + 1}
								</div>
								<span className="text-foreground">{task.property_address || task.location || task.property_name || 'Property'}</span>
								<span className="text-muted-foreground">
									• {task.scheduledTime}
								</span>
							</div>
						))}
						<div className="mt-4 pt-4 border-t border-border">
							<Button
								variant="outline"
								size="sm"
								iconName="Navigation"
								iconPosition="left"
							>
								Open in Maps
							</Button>
						</div>
					</div>
				) : (
					<p className="text-sm text-muted-foreground">
						Schedule multiple tasks to see route optimization suggestions.
					</p>
				)}
			</div>
		</div>
	);
};

export default ScheduleView;
